import type { ProducedToken } from "@-/dev.improve.haniverse.lib.sentence.drill";
import {
  sentenceWordsQueryFn,
  useGetSentenceWordsQueryKey,
} from "@-/dev.improve.haniverse.lib.sentence.words";
import { queryOptions, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useCallback } from "react";

/**
 * Turn a recognized Mandarin transcript into produced tokens, readings attached.
 *
 * The recognizer returns CHARACTERS with no spaces (`我喜欢吃苹果`), so the transcript has to be
 * segmented before it can be matched word-by-word — and each token needs its dictionary reading, or
 * the matcher's homophone rule has nothing to compare and a correctly-spoken word transcribed as
 * the wrong characters is rejected.
 *
 * Deliberately reuses the sentence-words query rather than reimplementing the lookup: that path
 * already runs jieba, re-splits unknown segments against the static `word` table, and resolves
 * readings from `word_definition` / `grapheme_definition`. Running the transcript through the SAME
 * segmentation the target sentence used also means the two token streams are directly comparable —
 * two different segmenters could disagree about where a word boundary falls and cause a correct
 * answer to miss.
 *
 * Fetched through the query client (not a hook) because a transcript arrives from an event, not a
 * render. Results are cached by transcript, so an interim that repeats an earlier prefix is free.
 */
export function useTranscriptProducedTokens(): (transcript: string) => Promise<ProducedToken[]> {
  const queryClient = useQueryClient();
  const getSentenceWordsQueryKey = useGetSentenceWordsQueryKey();

  return useCallback(
    async (transcript: string): Promise<ProducedToken[]> => {
      const trimmed = transcript.trim();
      if (trimmed.length === 0) {
        return [];
      }
      const words = await queryClient.fetchQuery(
        queryOptions({
          queryFn: sentenceWordsQueryFn,
          queryKey: getSentenceWordsQueryKey(trimmed),
        }),
      );
      return words.map(({ pinyin, word }) => ({ pinyin, word }));
    },
    [getSentenceWordsQueryKey, queryClient],
  );
}
